import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  ListItemIcon,
  Paper,
  styled,
  alpha,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, X, Trash2 } from "lucide-react";
import Fuse from "fuse.js";
import { NavItem } from "../types/common";
import { useAuthStore } from "../store/authstore";
import { navigationConfig } from "../config/navigation";
import { trackSearch } from "../lib/mixpanel";

interface SearchableNavItem extends NavItem {
  fullPath: string;
  breadcrumb: string;
  parentTitles: string[];
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: "8px",
  borderRadius: "12px",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(30, 41, 59, 0.95)"
      : "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  // Reduce padding for listbox
  "& .MuiAutocomplete-listbox": {
    padding: "4px", // Reduced from default 8px
    "& .MuiAutocomplete-option": {
      padding: "6px 12px", // Reduced padding for each option
      minHeight: "auto",
    },
  },
}));

const SearchOption = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  cursor: "pointer",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const RecentSearchHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 12px",
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

const NavigationSearch: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const { hasPermission, hasRoleType, hasRole } = useAuthStore();
  const [recentSearches, setRecentSearches] = useState<SearchableNavItem[]>(
    () => {
      const stored = localStorage.getItem("recentNavigationSearches");
      return stored ? JSON.parse(stored) : [];
    }
  );

  // Flatten navigation tree into searchable items
  const searchableItems = useMemo(() => {
    const items: SearchableNavItem[] = [];

    const hasAccess = (item: NavItem) => {
      return (
        (!item.requiredPermission || hasPermission(item.requiredPermission)) &&
        (!item.requiredRoleType || hasRoleType(item.requiredRoleType)) &&
        (!item.roles || item.roles.some((role: string) => hasRole(role)))
      );
    };

    const flattenItems = (navItems: NavItem[], parentTitles: string[] = []) => {
      navItems.forEach((item) => {
        if (!hasAccess(item)) return;

        if (item.path && !item.children) {
          items.push({
            ...item,
            fullPath: item.path,
            breadcrumb: [...parentTitles, item.title].join(" > "),
            parentTitles,
          });
        }

        if (item.children) {
          flattenItems(item.children, [...parentTitles, item.title]);
        }
      });
    };

    flattenItems(navigationConfig);
    return items;
  }, [hasPermission, hasRoleType, hasRole]);

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    const options = {
      keys: [
        { name: "title", weight: 0.7 },
        { name: "breadcrumb", weight: 0.3 },
      ],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 2,
    };
    return new Fuse(searchableItems, options);
  }, [searchableItems]);

  // Search results
  const searchResults = useMemo(() => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }
    return fuse.search(inputValue).map((result: { item: any }) => result.item);
  }, [inputValue, fuse]);

  const handleSelect = (event: any, value: SearchableNavItem | null) => {
    if (value?.fullPath) {
      // Track navigation search
      trackSearch(
        inputValue,
        1, // results count (one result was selected)
        'navigation',
        { destination: value.fullPath, breadcrumb: value.breadcrumb }
      );

      navigate(value.fullPath);

      // Update recent searches
      const updated = [
        value,
        ...recentSearches.filter((item) => item.fullPath !== value.fullPath),
      ].slice(0, 5);

      setRecentSearches(updated);
      localStorage.setItem("recentNavigationSearches", JSON.stringify(updated));
      setInputValue("");
    }
  };

  // Clear all recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentNavigationSearches");
  };

  // Remove single recent search
  const removeRecentSearch = (
    event: React.MouseEvent,
    pathToRemove: string
  ) => {
    event.stopPropagation();
    const updated = recentSearches.filter(
      (item) => item.fullPath !== pathToRemove
    );
    setRecentSearches(updated);
    localStorage.setItem("recentNavigationSearches", JSON.stringify(updated));
  };

  // Show recent searches when input is empty
  const displayOptions =
    inputValue.length >= 2 ? searchResults : recentSearches;
  const isShowingRecent = inputValue.length < 2 && recentSearches.length > 0;

  return (
    <Autocomplete
      freeSolo
      options={displayOptions}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={handleSelect}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option.title;
      }}
      filterOptions={(x) => x}
      noOptionsText={
        inputValue.length < 2 ? "Type to search..." : "No pages found"
      }
      PaperComponent={StyledPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search pages..."
          size="small"
          inputRef={inputRef}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Search size={18} style={{ marginRight: 10, color: theme.palette.primary.main, opacity: 0.9, flexShrink: 0 }} />
            ),
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5, flexShrink: 0 }}>
                {params.InputProps.endAdornment}
                {!isFocused && !inputValue && (
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.35, mr: 0.5 }}>
                    {['Ctrl', 'K'].map((key) => (
                      <Box
                        key={key}
                        component="kbd"
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          bgcolor: 'rgba(138,112,214,0.1)',
                          border: '1.5px solid rgba(138,112,214,0.28)',
                          borderRadius: '5px',
                          px: 0.6,
                          py: 0.15,
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          letterSpacing: '0.02em',
                          fontFamily: 'inherit',
                        }}
                      >
                        {key}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const isRecent = isShowingRecent;
        return (
          <React.Fragment key={option.fullPath}>
            {/* Show header only for first recent search item */}
            {isRecent &&
              recentSearches.findIndex((item) => item.fullPath === option.fullPath) === 0 && (
                <>
                  <RecentSearchHeader>
                    <span>Recent Searches</span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearRecentSearches();
                      }}
                      sx={{
                        padding: "2px",
                        "&:hover": {
                          color: "error.main",
                        },
                      }}
                      title="Clear all recent searches"
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </RecentSearchHeader>
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}
            <li {...props}>
              <SearchOption sx={{ width: "100%", position: "relative" }}>
                {option.icon && (
                  <ListItemIcon sx={{ minWidth: "auto" }}>
                    <option.icon size={18} />
                  </ListItemIcon>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.parentTitles.map((parent: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, idx: React.Key | null | undefined) => (
                      <React.Fragment key={idx}>
                        {parent}
                        <ChevronRight size={10} />
                      </React.Fragment>
                    ))}
                    <span style={{ fontWeight: 500 }}>{option.title}</span>
                  </Typography>
                </Box>
                {/* Remove button for recent searches */}
                {isRecent && (
                  <IconButton
                    size="small"
                    onClick={(e) => removeRecentSearch(e, option.fullPath)}
                    sx={{
                      padding: "4px",
                      opacity: 0.6,
                      "&:hover": {
                        opacity: 1,
                        color: "error.main",
                      },
                    }}
                  >
                    <X size={14} />
                  </IconButton>
                )}
              </SearchOption>
            </li>
          </React.Fragment>
        );
      }}
      sx={{
        width: "100%",
        maxWidth: "100%",
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
          boxShadow: `0 1px 6px rgba(0,0,0,0.06)`,
          minHeight: 44,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#fff",
            borderColor: alpha(theme.palette.primary.main, 0.75),
            boxShadow: `0 2px 10px rgba(0,0,0,0.08)`,
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}, 0 2px 10px rgba(0,0,0,0.08)`,
          },
          "& .MuiInputBase-input": {
            color: "rgba(0,0,0,0.82)",
            fontSize: "0.95rem",
            py: 1.25,
          },
          "& .MuiInputBase-input::placeholder": {
            color: "rgba(0,0,0,0.55)",
            opacity: 1,
            fontWeight: 500,
          },
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      }}
      slotProps={{
        popper: {
          sx: {
            "& .MuiPaper-root": {
              maxHeight: { xs: "60vh", sm: "400px" },
            },
          },
        },
      }}
    />
  );
};

export default NavigationSearch;
